/**
 * Theme Configuration - Single Source of Truth
 * 
 * All theme definitions for the application.
 * Used by: ThemeSwitcher, Onboarding, ThemePreviewCard
 * 
 * INVARIANT: Theme changes must follow theme-architecture.md V2
 * - Onboarding: preview only via sandboxed card, actual setTheme() on completion
 * - ThemeSwitcher: live setTheme() + DB sync (authorized writer)
 */

export interface ThemeDefinition {
  id: string;
  name: string;
  description: string;
  isPremium: boolean;
  /**
   * Tailwind classes for color circle previews in lists
   */
  previewColors: {
    bg: string;
    card: string;
    primary: string;
  };
  /**
   * Hex colors for sandboxed preview rendering (inline styles)
   * Used by ThemePreviewCard to avoid global CSS mutation
   */
  hexColors: {
    bg: string;
    card: string;
    primary: string;
    text: string;
    textMuted: string;
    border: string;
  };
}

export const THEMES: ThemeDefinition[] = [
  { 
    id: 'charcoal-pro', 
    name: 'Charcoal Pro', 
    description: 'Professional & focused',
    isPremium: false,
    previewColors: {
      bg: 'bg-[#171B21]',
      card: 'bg-[#1E2329]',
      primary: 'bg-[#3B9EFF]'
    },
    hexColors: {
      bg: '#171B21',
      card: '#1E2329',
      primary: '#3B9EFF',
      text: '#F8FAFC',
      textMuted: '#94A3B8',
      border: '#2D3748'
    }
  },
  { 
    id: 'ivory-scholar', 
    name: 'Ivory Scholar', 
    description: 'Light & academic',
    isPremium: false,
    previewColors: {
      bg: 'bg-[#F9F7F4]',
      card: 'bg-[#FEFDFB]',
      primary: 'bg-[#1D7DC7]'
    },
    hexColors: {
      bg: '#F9F7F4',
      card: '#FEFDFB',
      primary: '#1D7DC7',
      text: '#1A1A1A',
      textMuted: '#6B7280',
      border: '#E5E7EB'
    }
  },
  { 
    id: 'forest-study', 
    name: 'Forest Study', 
    description: 'Warm & cozy',
    isPremium: false,
    previewColors: {
      bg: 'bg-[#141211]',
      card: 'bg-[#1C1917]',
      primary: 'bg-[#3C8C6B]'
    },
    hexColors: {
      bg: '#141211',
      card: '#1C1917',
      primary: '#3C8C6B',
      text: '#FAFAF9',
      textMuted: '#A8A29E',
      border: '#292524'
    }
  },
  { 
    id: 'cafe-mocha', 
    name: 'Cafe Mocha', 
    description: '☕ Warm coffee vibes',
    isPremium: true,
    previewColors: {
      bg: 'bg-[#140D0C]',
      card: 'bg-[#1F1311]',
      primary: 'bg-[#8B5D43]'
    },
    hexColors: {
      bg: '#140D0C',
      card: '#1F1311',
      primary: '#8B5D43',
      text: '#FDF8F6',
      textMuted: '#A8988E',
      border: '#3D2E28'
    }
  },
  { 
    id: 'royal-scholar', 
    name: 'Royal Scholar', 
    description: '👑 Professional elegance',
    isPremium: true,
    previewColors: {
      bg: 'bg-[#F7F6FC]',
      card: 'bg-[#FFFFFF]',
      primary: 'bg-[#6D28D9]'
    },
    hexColors: {
      bg: '#F7F6FC',
      card: '#FFFFFF',
      primary: '#6D28D9',
      text: '#1F1F2E',
      textMuted: '#6B7280',
      border: '#E5E4EB'
    }
  },
  { 
    id: 'golden-scholar', 
    name: 'Golden Scholar', 
    description: '🏆 Luxurious gold',
    isPremium: true,
    previewColors: {
      bg: 'bg-[#1A1713]',
      card: 'bg-[#242018]',
      primary: 'bg-[#EAB308]'
    },
    hexColors: {
      bg: '#1A1713',
      card: '#242018',
      primary: '#EAB308',
      text: '#FEFCE8',
      textMuted: '#A8A29E',
      border: '#3D3730'
    }
  },
  { 
    id: 'rose-focus', 
    name: 'Rose Focus', 
    description: '🌸 Sophisticated rose',
    isPremium: true,
    previewColors: {
      bg: 'bg-[#1A1217]',
      card: 'bg-[#241A1F]',
      primary: 'bg-[#E11D74]'
    },
    hexColors: {
      bg: '#1A1217',
      card: '#241A1F',
      primary: '#E11D74',
      text: '#FDF2F8',
      textMuted: '#A8919A',
      border: '#3D2A34'
    }
  },
];

/**
 * Get theme by ID, fallback to charcoal-pro
 */
export function getThemeById(themeId: string): ThemeDefinition {
  return THEMES.find(t => t.id === themeId) || THEMES[0];
}

/**
 * Get free themes only
 */
export function getFreeThemes(): ThemeDefinition[] {
  return THEMES.filter(t => !t.isPremium);
}

/**
 * Get premium themes only
 */
export function getPremiumThemes(): ThemeDefinition[] {
  return THEMES.filter(t => t.isPremium);
}

/**
 * Default theme ID
 */
export const DEFAULT_THEME_ID = 'charcoal-pro';
